import React from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { LifeBuoy } from "lucide-react";

export default function PortalTickets() {
  const [tickets, setTickets] = React.useState([]);
  const [filter, setFilter] = React.useState("open");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      const r = await api.get("/portal/me/tickets");
      setTickets(r.data);
      setLoading(false);
    })();
  }, []);

  const filtered = tickets.filter((t) => {
    if (filter === "all") return true;
    if (filter === "closed") return ["Resolved", "Closed"].includes(t.status);
    return !["Resolved", "Closed"].includes(t.status);
  });

  return (
    <div className="space-y-6" data-testid="portal-tickets-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Support</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Tickets</h1>
          <p className="text-slate-500 text-sm mt-1">Read-only view of every ticket Xentra has on your account.</p>
        </div>
        <div className="flex gap-1">
          {[["open", "Open"], ["closed", "Closed"], ["all", "All"]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`portal-tickets-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                filter === v ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="border-slate-200 p-12 text-center">
          <LifeBuoy className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No tickets in this view</div>
          <div className="text-sm text-slate-500 mt-1">Need help? Contact your account manager directly.</div>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Priority</th>
                  <th className="px-4 py-2">Product</th>
                  <th className="px-4 py-2">Opened</th>
                  <th className="px-4 py-2">SLA</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-blue-50/30" data-testid={`portal-ticket-${t.id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-900">{t.subject || t.title}</div>
                      {t.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{t.description}</div>}
                    </td>
                    <td className="px-4 py-2"><StatusBadge value={t.status} /></td>
                    <td className="px-4 py-2 text-slate-600">{t.priority}</td>
                    <td className="px-4 py-2 text-slate-600 text-[12px]">{t.product_name || t.stax_product_name || "—"}</td>
                    <td className="px-4 py-2 text-slate-500 font-mono text-[12px]">{fmtDate(t.created_at)}</td>
                    <td className="px-4 py-2 text-slate-500 font-mono text-[12px]">{fmtDate(t.sla_due || t.sla_due_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ value }) {
  const palette = {
    "New": "bg-blue-50 text-blue-700 border-blue-100",
    "Open": "bg-amber-50 text-amber-700 border-amber-100",
    "Waiting on Customer": "bg-orange-50 text-orange-700 border-orange-100",
    "Waiting on Internal": "bg-violet-50 text-violet-700 border-violet-100",
    "Resolved": "bg-emerald-50 text-emerald-700 border-emerald-100",
    "Closed": "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase font-mono tracking-wider rounded border ${palette[value] || palette.Open}`}>
      {value}
    </span>
  );
}
